import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiArrowLeft, FiRefreshCw } from "react-icons/fi";
import { FaEdit } from "react-icons/fa";
import api from "api";
import { formatDate, debug, download } from "utils";
import FormalitiesRequestStatusDropdown from "../helpers/FormalitiesRequestStatusDropdown";
import FormalityRequestStatusTimeline from "../helpers/FormalityRequestStatusTimeline";
import AdminFormalityActionsButtons from "./inc/AdminFormalityActionsButtons";
import AdminFormalityBillingCard from "./inc/AdminFormalityBillingCard";
import AdminFormalityUserCard from "./inc/AdminFormalityUserCard";
import AdminFormalityRightColumn from "./inc/AdminFormalityRightColumn";
import AdminFormalityRequestEditModal from "./inc/AdminFormalityRequestEditModal";
import AdminFormalityRequestMessagesCard from "./inc/AdminFormalityRequestMessagesCard";
import AdminFormalityRequestLogItemsCard from "./inc/AdminFormalityRequestLogItemsCard";

import SendPaymentLinkModal from 'components/orders/modals/SendPaymentLinkModal';


// import ContactClientModal from "./helpers/ContactClientModal";
import CustomerMessageModal from "components/customers/helpers/CustomerMessageModal";
import FileModal from "./helpers/FileModal";
import { applyConditionalDisplay } from "components/formbuilder/utils/applyConditionalDisplay";
import useFormalityAutoStatusTransition from './hooks/useFormalityAutoStatusTransition'
import useCustomerMessage from './hooks/useCustomerMessage'
import FormBuilderSummary from "components/formbuilder/FormBuilderSummary";
import Swal from "sweetalert2";
import "./css/admin-formality-request.css";
export default function AdminFormalityRequest() {
  const { frId } = useParams();
  const config = useSelector(state => state.config);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [template, setTemplate] = useState(null);
  const [schema, setSchema] = useState(null);
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [requestUser, setResquestUser] = useState(null);
  const [assignee, setAssignee] = useState(null);
  const [showAdminFormalityRequestEditModal, setShowAdminFormalityRequestEditModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [currentField, setCurrentField] = useState(null);
  const [currentFileAnalysis, setCurrentFileAnalysis] = useState(null);
  const [validationsErrors, setValidationsErrors] = useState(null);
  const [mailMessages, setMailMessages] = useState([]);
  const [logItems, setLogItems] = useState([]);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const toastSuccess = (title) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };
  // --------------------------------------------------
  // LOAD
  // --------------------------------------------------
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/formalities/requests/${frId}`);
      setRequest(data);
      setTemplate(data.template || null);
      setSchema(applyConditionalDisplay(data.template?.format?.schema, data.frData));
      setOrder(data.order || null);
      setCustomer(data.customer || null);
      setResquestUser(data.user || null);
      loadMailMessages();
      loadLogItems();
    } catch (e) {

      setError("Impossible de charger la demande.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!frId) return;
    load();
  }, [frId]);
  useEffect(() => {
    if (!request) {
      return;
    }
  }, [request])
  // --------------------------------------------------
  // ACTIONS
  // --------------------------------------------------
  const handleAssign = async () => {
    try {
      const res = await api.post(`/admin/formalities/request/${frId}/assign`);
      if (res.data?.assignee) {
        setRequest(r => ({ ...r, frStatus: res.data.frStatus, frAssignedTo: res.data.frAssignedTo, assignee: res.data.assignee }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSendPaymentLink = async () => {
    setShowPaymentLinkModal(true);
  };
  const handleEditRequest = () => {
    setShowAdminFormalityRequestEditModal(true);
  }
  const handleUpdateStatus = async (status) => {
    const res = await api.put(`/admin/formalities/request/${request.frId}`, {
      frStatus: status,
    });
    if (res && res.data) {
      setRequest(prev => ({
        ...prev,
        frStatus: res.data.frStatus
      }));
    }
  };
  // mise en conformité du statut avec frIsCompliant
  useFormalityAutoStatusTransition(request, handleUpdateStatus);
  const handleFileClick = (file, field) => {
    const fileAnalysis = request.frAnalysis?.[field.parents?.[0]]?.[field.name]?.files?.[file.path] || null;

    setCurrentFile(file);
    setCurrentField(field);
    setCurrentFileAnalysis(fileAnalysis);
    setShowFileModal(true);
  };
  const handleRejectFile = async (file, reason) => {

    const res = await api.post(`/admin/formalities/request/${frId}/analysis`, {
      status: 'rejected',
      field: currentField,
      file: file,
      reason: reason,
    });
    if (res.data?.frAnalysis) {
      setRequest(prev => ({
        ...prev,
        frAnalysis: res.data.frAnalysis,
        frIsCompliant: res.data.frIsCompliant,
        frIsCompliantReason: res.data.frIsCompliantReason,
        frStatus: res.data.frStatus
      }));
    }
    setShowFileModal(false);
    setCurrentFile(null);
    setCurrentField(null);
  }
  const handleAcceptFile = async (file) => {
    const res = await api.post(`/admin/formalities/request/${frId}/analysis`, {
      status: 'accepted',
      field: currentField,
      file: file,
    });
    if (res.data?.frAnalysis) {
      setRequest(prev => ({
        ...prev,
        frAnalysis: res.data.frAnalysis,
        frIsCompliant: res.data.frIsCompliant,
        frIsCompliantReason: res.data.frIsCompliantReason,
        frStatus: res.data.frStatus
      }));
    }
    setShowFileModal(false);
    setCurrentFile(null);
    setCurrentField(null);
  }
  const handleDownloadDocuments = async () => {
    download(`/admin/formalities/request/${frId}/documents/download`, true);
  }
  const handleDeleteFile = async () => {
    if (!currentFile) {
      return;
    }
    const conf = await Swal.fire({
      title: 'Confirmation',
      html: `Confirmez-vous la suppression du document<br /><strong>« ${currentFile.name} »</strong>&nbsp;?`,
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-danger me-2',
        cancelButton: 'btn btn-outline-secondary',
      },
      icon: undefined, // explicite : aucune icône
    });
    if (!conf.isConfirmed) {
      return;
    }

    setShowFileModal(false);
    setCurrentFile(null);
    setCurrentField(null);

    api.handleError = false;
    try {
      const res = await api.post(`/admin/formalities/request/${request.frId}/document/delete`, {
        field: currentField,
        file: currentFile,
      });
      load();
    } catch (err) {
      if (err.response.data.error.message) {
        if (err?.response?.data?.error?.message) {
          Swal.fire({
            title: 'Erreur',
            text: err.response.data.error.message,
            icon: undefined, // explicite : aucune icône
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-secondary',
            },
          });
        }
      }
    }
  };
  const handleSendDocumentsToCustomer = () => {
    openMessageFromTemplate("sendDocuments");
    return;
  };
  const handleAfterSend = (context) => {
    loadMailMessages();
    loadLogItems();
    if (context === 'rejection') {
      Swal.fire({
        title: "Document(s) non conforme(s)",
        text: "Souhaitez-vous passer la demande au statut « En attente client » ?",
        showCancelButton: true,
        confirmButtonText: "Oui, passer en attente",
        cancelButtonText: "Non",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await handleUpdateStatus("waiting");
        }
      });
    }
    if (context === 'sendDocuments') {
      Swal.fire({
        title: "Demande finalisée",
        html: "Souhaitez-vous passer la demande<br /> au statut « terminée » ?",
        showCancelButton: true,
        confirmButtonText: "Oui, passer à terminée",
        cancelButtonText: "Non",
        reverseButtons: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await handleUpdateStatus("completed");
        }
      });
    }
  };
  const loadMailMessages = async () => {
    const res = await api.get(`/admin/formalities/request/${frId}/messages`);
    if (res && res.data) {
      setMailMessages(res.data);
    }
  };
  const loadLogItems = async () => {
    const res = await api.get(`/admin/formalities/request/${frId}/logItems`);
    if (res && res.data) {
      setLogItems(res.data);
    }
  };
  const handleResponseDocumentsChanged = (documents) => {
    setRequest(prev => ({
      ...prev,
      response_documents: documents
    }));
  }
  const {
    openMessageFromTemplate,
    customerMessageModalIsOpen,
    message,
    attachments,
    openBlankMessage,
    sendMessage,
    closeMessage,
  } = useCustomerMessage({ request, config, onAfterSend: handleAfterSend });
  // --------------------------------------------------
  // RENDER GUARDS
  // --------------------------------------------------
  if (loading) {
    return <div id="admin-formality-request">Chargement…</div>;
  }
  if (error) {
    return <div id="admin-formality-request">{error}</div>;
  }
  if (!request) return null;
  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------
  return (
    <div id="admin-formality-request">
      <div className="afr-container-fluid">
        {/* COLONNE GAUCHE */}
        <div className="afr-main">
          <div className="mb-2">
            <Link to="/admin/formalities/requests/list" className="small">
              <FiArrowLeft size={18} /> Retour aux demandes
            </Link>
          </div>
          {/* HEADER */}
          <div className="afr-header">
            <div className="d-flex align-items-start gap-4"          >
              <div className="flex-grow-1">
                <h1 className="text-primary text-start fs-5 mb-0 pb-0">
                  {request.frCustomName || template?.ftName || "(Sans titre)"}
                </h1>
                <div className="small text-muted">
                  {template?.category?.fcName} /  {template?.ftName}
                </div>

                <AdminFormalityActionsButtons
                  request={request}
                  onAssign={handleAssign}
                  onSendPaymentLink={null}
                  onOpenCustomerMessage={openMessageFromTemplate}
                  onMarkAsProcessed={() => handleUpdateStatus('processed')}
                  onUpdateStatus={(status) => handleUpdateStatus(status)}
                  onSendDocumentsToCustomer={handleSendDocumentsToCustomer}
                />
              </div>
              <div className="d-flex gap-2">
                <button
                  className={`btn btn-outline-primary  btn-sm`}
                  onClick={load}
                >
                  <FiRefreshCw
                    style={{ top: "-2px" }}
                    className="position-relative"
                  />
                </button>
                <button
                  className={`btn ${request.validation?.validated === true
                    ? "btn-outline-primary"
                    : "btn-danger"
                    } btn-sm`}
                  onClick={handleEditRequest}
                  disabled={request.frAssignedTo === ""}
                >
                  <FaEdit
                    style={{ top: "-2px" }}
                    className="position-relative"
                  />
                </button>
              </div>
              <div>
                <FormalitiesRequestStatusDropdown
                  selected={request.frStatus}
                  readOnly
                  size="sm"
                />
                <ul className="small text-muted list-unstyled mt-2">
                  <li>Créée le : <strong>{formatDate(request.frCreatedAt)}</strong></li>
                  <li>Soumise le :<strong>{request.frSubmittedAt ? formatDate(request.frSubmittedAt) : "(non soumise)"}</strong></li>
                  <li>Assignée à : <strong>{request.assignee?.userFirstname || "(personne)"}</strong></li>
                </ul>
              </div>
            </div>
            <div className="mb-0 px-1 pe-1">
              <FormalityRequestStatusTimeline
                currentStatus={request?.frStatus}
              />
            </div>
          </div>
          <div className="afr-summary">
            <div className="row g-3">
              <div className="col-md-4 mb-3 small">
                <AdminFormalityUserCard
                  user={requestUser}
                  onBtnMessageClick={
                    request?.frAssignedTo ? openBlankMessage : undefined
                  }
                />
              </div>
              <div className="col-md-4 mb-3 small">
                <AdminFormalityBillingCard order={order} />
              </div>
              <div className="col-md-4 mb-3 small" style={{ maxHeight: 500, overflowY: 'auto' }}>
                <AdminFormalityRequestLogItemsCard items={logItems} columns={['liMessage', 'liCreatedAt']} />
              </div>
              <div className="col-12 mb-3  small" style={{ maxHeight: 400, overflowY: 'auto' }}>
                <AdminFormalityRequestMessagesCard messages={mailMessages} />
              </div>
            </div>
          </div>
          {/* ACTIONS */}

        </div>
        {/* COLONNE DROITE */}
        <AdminFormalityRightColumn
          template={template}
          request={request}
          schema={schema}
          onFileClick={handleFileClick}
          onDownloadDocuments={handleDownloadDocuments}
          onResponseDocumentsChanged={handleResponseDocumentsChanged}
        />
      </div>

      {request && showFileModal && (
        <FileModal
          show
          file={currentFile}
          fileAnalysis={currentFileAnalysis}
          readOnly={request.frAssignedTo === ''}
          requestId={request.frId}
          onReject={handleRejectFile}
          onAccept={handleAcceptFile}
          onClose={() => setShowFileModal(false)}
          onDelete={['in_progress', 'resubmitted'].includes(request.frStatus) ? handleDeleteFile : null}
        />
      )}
      {customerMessageModalIsOpen && (
        <CustomerMessageModal
          sender={{
            name: 'Fortium Formalités',
            email: 'noreply@fortium.fr',
          }}
          subject={message.subject}
          body={message.body}
          attachments={attachments}
          onSend={sendMessage}
          onClose={closeMessage}
        />
      )}
      {showAdminFormalityRequestEditModal &&
        <AdminFormalityRequestEditModal
          request={request}        // objet backend complet
          onClose={() => { setShowAdminFormalityRequestEditModal(false) }}
          onSaved={() => load()}
        />}

      <SendPaymentLinkModal
        show={showPaymentLinkModal}
        order={order}
        onClose={() => setShowPaymentLinkModal(false)}
        onSuccess={() => {
          setShowPaymentLinkModal(false);
          load();
        }}
      />
    </div>
  );
}
